// InquiryPage.jsx
import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import styles from "./Inquiry.module.css";

export default function InquiryPage() {
  const { user } = useAuth();
  const isLoggedIn = !!user;

  // 공통 입력
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");

  // 비로그인용 입력
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");

  // 로그인 사용자용 리스트/상세
  const [list, setList] = useState({
    content: [],
    totalPages: 0,
    number: 0,
    totalElements: 0,
  });
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(null);

  // === 목록 로딩 (로그인 사용자 전용) ==============================
  const loadList = () => {
    if (!isLoggedIn) {
      setList({ content: [], totalPages: 0, number: 0, totalElements: 0 });
      setSelected(null);
      return;
    }

    fetch(`/api/support/inquiries/my?page=${page}&size=10`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then(setList);
  };

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, isLoggedIn]);

  const loadDetail = (id) => {
    if (!isLoggedIn) return;
    fetch(`/api/support/inquiries/${id}`, { credentials: "include" })
      .then((res) => res.json())
      .then(setSelected);
  };

  // === 문의 등록 ==============================================
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim() || !content.trim()) {
      alert("제목과 내용을 입력해 주세요.");
      return;
    }

    // 1) 로그인 사용자: 기존 API 사용
    if (isLoggedIn) {
      const res = await fetch("/api/support/inquiries", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ title, content }),
      });

      if (res.ok) {
        setTitle("");
        setContent("");
        loadList();
        alert("문의가 등록되었습니다.");
      } else {
        alert("문의 등록에 실패했습니다.");
      }
      return;
    }

    // 2) 비로그인 사용자: 이메일 기반 게스트 문의
    if (!guestEmail.trim()) {
      alert("답변을 받을 이메일을 입력해 주세요.");
      return;
    }

    const body = {
      name: guestName,
      email: guestEmail,
      title,
      content,
    };

    // ⚠️ 서버 쪽에서 /support/inquiries/guest-email 또는 /guest를 여기와 맞춰 구현 필요
    const res = await fetch("/api/support/inquiries/guest", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });

    if (res.ok) {
      setTitle("");
      setContent("");
      // 비로그인은 리스트 사용 안 함
      alert("문의가 접수되었습니다.\n입력하신 이메일로 답변을 보내드립니다.");
    } else {
      alert("문의 등록에 실패했습니다.");
    }
  };

  return (
    <main className={styles.page}>
      {/* 상단 제목/설명 */}
      <h1 className={styles.title}>1:1 문의</h1>
      <section className={styles.formSection}>
        <p className={styles.subtitle}>
          서비스 관련 문의를 남겨 주세요. 전문 상담원이 최대한 빠르게 답변해 드립니다.
        </p>

        {/* 문의 작성 폼 */}
        <form className={styles.form} onSubmit={handleSubmit}>
          {/* 비로그인 사용자용: 이름 / 이메일 */}
          {!isLoggedIn && (
            <div className={styles.guestRow}>
              <label>
                이름 <span className={styles.required}>*</span>
                <input
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  placeholder="예: 홍길동"
                />
              </label>
              <label>
                이메일 <span className={styles.required}>*</span>
                <input
                  type="email"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  placeholder="답변을 받을 이메일 주소"
                  required
                />
              </label>
            </div>
          )}

          <label>
            제목 <span className={styles.required}>*</span>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="문의 제목을 입력해 주세요"
              required
            />
          </label>

          <label>
            내용 <span className={styles.required}>*</span>
            <textarea
              rows={7}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="문의하실 내용을 자세히 작성해 주세요."
              required
            />
          </label>

          <div className={styles.actions}>
            <button type="submit">문의하기</button>
          </div>
        </form>
      </section>

      {/* 로그인 사용자만 이전 문의 내역/상세 표시 */}
      {isLoggedIn && (
        <section className={styles.listSection}>
          <h2 className={styles.subTitle}>이전 문의 내역</h2>

          <table className={styles.table}>
            <thead>
              <tr>
                <th style={{ width: "60px" }}>번호</th>
                <th>제목</th>
                <th style={{ width: "120px" }}>상태</th>
                <th style={{ width: "160px" }}>작성일</th>
              </tr>
            </thead>
            <tbody>
              {list.content.map((item, idx) => (
                <tr key={item.id} onClick={() => loadDetail(item.id)}>
                  <td>
                    {list.totalElements - (list.number * 10 + idx)}
                  </td>
                  <td>{item.title}</td>
                  <td>{item.status}</td>
                  <td>
                    {(item.createdAt || item.created_at || "").slice(0, 10)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className={styles.pagination}>
            <button
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
            >
              이전
            </button>
            <span>
              {page + 1} / {list.totalPages || 1}
            </span>
            <button
              disabled={page + 1 >= list.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              다음
            </button>
          </div>

          {selected && (
            <div className={styles.detail}>
              <h3 className={styles.detailTitle}>{selected.inquiry.title}</h3>
              <div className={styles.thread}>
                {selected.messages.map((m) => (
                  <div
                    key={m.id}
                    className={
                      m.senderRole === "ADMIN"
                        ? `${styles.msg} ${styles.admin}`
                        : `${styles.msg} ${styles.user}`
                    }
                  >
                    <div className={styles.meta}>
                      <span>
                        {m.senderRole === "ADMIN" ? "관리자" : "나"}
                      </span>
                      <span>
                        {(m.createdAt || m.created_at || "")
                          .slice(0, 16)
                          .replace("T", " ")}
                      </span>
                    </div>
                    <div className={styles.body}>
                      {m.content.split("\n").map((line, idx) => (
                        <p key={idx}>{line}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}
    </main>
  );
}
